import { businessDate, formatJalali } from '@sakhtar/calendar';

/**
 * Read-only Jalali rendering of a Gregorian-ISO date string — the display
 * boundary, not a second storage format (SOT §9, same rule as
 * JalaliDateInput). Never throws: an empty or malformed value renders as
 * '—' rather than crashing a list/table cell.
 */
export function displayJalaliDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return formatJalali(businessDate(value.slice(0, 10)), { persianDigits: true });
  } catch {
    return value;
  }
}

/**
 * FE-P0 FE-HYGIENE-1: `YYYY-MM` (a Gregorian month bucket, e.g. a chart's
 * per-month grouping key) → Jalali "year/month" only, via the same
 * read-only display boundary as {@link displayJalaliDate} — never a new
 * `packages/calendar` function; `formatJalali` is numeric-only by design
 * (JL-2) and a month-only variant would be a display decision taken
 * outside it, so this reuses the existing full-date formatter and drops
 * the day segment.
 */
export function displayJalaliMonth(value: string): string {
  const full = displayJalaliDate(`${value}-01`);
  if (full === '—') return full;
  const [year, month] = full.split('/');
  return year && month ? `${year}/${month}` : full;
}

/**
 * Same rule as {@link displayJalaliDate}, for a `YYYY-MM-DDTHH:mm`-shaped
 * value (the same wire format `JalaliDateTimeInput` produces): the date
 * part renders Jalali, the clock-time part is appended as-is (time has no
 * calendar system to localize).
 */
export function displayJalaliDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const [datePart, timePart] = value.split('T');
  const dateLabel = displayJalaliDate(datePart);
  if (dateLabel === '—') return value;
  return timePart ? `${dateLabel} ${timePart.slice(0, 5)}` : dateLabel;
}
